import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Crown, Gift, Star, Zap, TrendingUp, History, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface LoyaltyPoints {
  total_points: number;
  lifetime_points: number;
  tier: string;
}

interface LoyaltyReward {
  id: string;
  name: string;
  description: string;
  points_cost: number;
  reward_type: string;
  reward_value: any;
  stock: number | null;
}

interface LoyaltyTransaction {
  id: string;
  points: number;
  transaction_type: string;
  source: string;
  description: string;
  created_at: string;
}

const tierConfig = {
  bronze: { color: 'bg-amber-700', icon: Star, nextTier: 'silver', pointsNeeded: 1000 },
  silver: { color: 'bg-gray-400', icon: Star, nextTier: 'gold', pointsNeeded: 5000 },
  gold: { color: 'bg-yellow-500', icon: Crown, nextTier: 'platinum', pointsNeeded: 20000 },
  platinum: { color: 'bg-purple-500', icon: Crown, nextTier: 'diamond', pointsNeeded: 50000 },
  diamond: { color: 'bg-cyan-400', icon: Sparkles, nextTier: null, pointsNeeded: null },
};

export function LoyaltyProgram() {
  const { user } = useAuth();
  const [points, setPoints] = useState<LoyaltyPoints | null>(null);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchLoyaltyData();
    }
  }, [user]);

  const fetchLoyaltyData = async () => {
    try {
      // Fetch user's loyalty points
      const { data: pointsData } = await supabase
        .from('loyalty_points')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      setPoints(pointsData || { total_points: 0, lifetime_points: 0, tier: 'bronze' });

      // Fetch available rewards
      const { data: rewardsData } = await supabase
        .from('loyalty_rewards')
        .select('*')
        .eq('is_active', true)
        .order('points_cost');

      setRewards(rewardsData || []);

      // Fetch recent transactions
      const { data: transData } = await supabase
        .from('loyalty_transactions')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(10);

      setTransactions(transData || []);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const redeemReward = async (reward: LoyaltyReward) => {
    if (!points || points.total_points < reward.points_cost) {
      toast.error('Not enough points');
      return;
    }

    setRedeeming(reward.id);
    try {
      // Deduct points
      const { error: updateError } = await supabase
        .from('loyalty_points')
        .update({ 
          total_points: points.total_points - reward.points_cost,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user?.id);

      if (updateError) throw updateError;

      // Record transaction
      await supabase.from('loyalty_transactions').insert({
        user_id: user?.id,
        points: -reward.points_cost,
        transaction_type: 'redeem',
        source: 'redemption',
        description: `Redeemed: ${reward.name}`,
      });

      toast.success(`Successfully redeemed ${reward.name}!`);
      fetchLoyaltyData();
    } catch (error) {
      toast.error('Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  const currentTier = points?.tier || 'bronze';
  const tierInfo = tierConfig[currentTier as keyof typeof tierConfig];
  const TierIcon = tierInfo.icon;
  const progressToNext = tierInfo.pointsNeeded 
    ? Math.min(((points?.lifetime_points || 0) / tierInfo.pointsNeeded) * 100, 100)
    : 100;

  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="h-24 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Points Summary */}
      <Card className="bg-gradient-to-br from-primary/20 via-card to-card border-primary/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-full ${tierInfo.color}`}>
                <TierIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold capitalize">{currentTier} Member</h3>
                <p className="text-muted-foreground text-sm">
                  {points?.lifetime_points?.toLocaleString() || 0} lifetime points
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-primary">
                {points?.total_points?.toLocaleString() || 0}
              </div>
              <p className="text-sm text-muted-foreground">Available Points</p>
            </div>
          </div>

          {tierInfo.nextTier && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress to {tierInfo.nextTier}</span>
                <span>{tierInfo.pointsNeeded?.toLocaleString()} points needed</span>
              </div>
              <Progress value={progressToNext} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="rewards" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="rewards" className="flex items-center gap-2">
            <Gift className="h-4 w-4" />
            Rewards
          </TabsTrigger>
          <TabsTrigger value="earn" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Earn Points
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rewards" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.map((reward) => (
              <Card key={reward.id} className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{reward.name}</h4>
                      <p className="text-sm text-muted-foreground">{reward.description}</p>
                    </div>
                    <Badge variant="secondary" className="bg-primary/20 text-primary">
                      {reward.points_cost.toLocaleString()} pts
                    </Badge>
                  </div>
                  <Button
                    className="w-full"
                    variant={(points?.total_points || 0) >= reward.points_cost ? 'default' : 'outline'}
                    disabled={(points?.total_points || 0) < reward.points_cost || redeeming === reward.id}
                    onClick={() => redeemReward(reward)}
                  >
                    {redeeming === reward.id ? 'Redeeming...' : 'Redeem'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="earn" className="space-y-4">
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Ways to Earn Points</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { action: 'Daily Login', points: 10, icon: Star },
                { action: 'Place a Bet', points: 5, icon: TrendingUp },
                { action: 'Win a Bet', points: 25, icon: Zap },
                { action: 'Purchase a Tip', points: 50, icon: Gift },
                { action: 'Refer a Friend', points: 500, icon: Crown },
                { action: 'Complete a Challenge', points: '100-500', icon: Sparkles },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <item.icon className="h-5 w-5 text-primary" />
                    <span>{item.action}</span>
                  </div>
                  <Badge variant="outline">+{item.points} pts</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              {transactions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No transactions yet</p>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className="font-medium">{tx.description || tx.source}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={tx.points > 0 ? 'text-green-500' : 'text-red-500'}>
                        {tx.points > 0 ? '+' : ''}{tx.points}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
